import React, { useState,useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Clapperboard,
  Star,
  TrendingUp,
  MoreVertical,
  Plus,
  Eye,
  Film,
  Video,
  MapPin
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./DirectorDashboard.css";
import { fetchCastingDirectorDashboardApi } from "../../../services/api";

// ==========================================
// 📊 STATIC DUMMY DATA (Update with API later)
// ==========================================

// ==========================================
// 🎨 MAIN COMPONENT
// ==========================================

const DirectorDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
const [loading, setLoading] = useState(true);

useEffect(() => {

  fetchDashboard();

}, []);

const fetchDashboard = async () => {

  try {

    const response =
      await fetchCastingDirectorDashboardApi();

    setDashboardData(
      response.data.data
    );

  } catch (error) {

    console.log(error);

  } finally {

    setLoading(false);

  }

};

  const getStatusBadge = (status) => {
    const s = status.toLowerCase();
    if (s === "pending" || s === "applied") return <span className="dd-badge badge-yellow">{status}</span>;
    if (s === "reviewed") return <span className="dd-badge badge-blue">Reviewed</span>;
    if (s === "shortlisted") return <span className="dd-badge badge-green">Shortlisted</span>;
    if (s === "rejected") return <span className="dd-badge badge-red">Rejected</span>;
    return <span className="dd-badge badge-gray">{status}</span>;
  };

  const getTypeBadge = (type) => {
    const t = type.toUpperCase();
    if (t.includes("ONLINE")) return <span className="dd-type-cell"><Video size={14}/> Online</span>;
    if (t.includes("SLOT") || t.includes("WALK")) return <span className="dd-type-cell"><MapPin size={14}/> Walk In</span>;
    return <span className="dd-type-cell">{type}</span>;
  };

  return (
    <div className="dd-wrapper">
      <div className="dd-container">
        
        {/* HEADER */}
        <div className="dd-header">
          <div>
            <h1 className="dd-title">Director Dashboard</h1>
            <p className="dd-subtitle">Manage your vision. Review applicants and active roles seamlessly.</p>
          </div>
          <button className="dd-btn-primary" onClick={() => navigate('/dashboard/post-roles')}>
            <Plus size={18} /> Post New Role
          </button>
        </div>

        {/* KPI CARDS (Borderless & Shadowed) */}
        <div className="dd-kpi-grid">
          {[
            {
              title: "Total Projects",
              value: dashboardData?.kpis?.totalProjects || 0,
              icon: <Clapperboard />,
              trend: "+1 this month"
            },

            {
              title: "Active Roles",
              value: dashboardData?.kpis?.activeRoles || 0,
              icon: <Film />,
              trend: "Active"
            },

            {
              title: "Total Applicants",
              value: dashboardData?.kpis?.totalApplicants || 0,
              icon: <Users />,
              trend: "+20%"
            },

            {
              title: "Shortlisted",
              value: dashboardData?.kpis?.shortlisted || 0,
              icon: <Star />,
              trend: "Selected"
            }

          ].map((kpi, index) => (
            <div className="dd-card dd-kpi-card" key={index}>
              <div className="dd-kpi-top">
                <div className="dd-kpi-icon">{kpi.icon}</div>
                <div className="dd-kpi-trend">
                  <TrendingUp size={14} /> <span>{kpi.trend}</span>
                </div>
              </div>
              <h3 className="dd-kpi-value">{kpi.value}</h3>
              <p className="dd-kpi-label">{kpi.title}</p>
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION: CHART & ACTIVE ROLES */}
        <div className="dd-middle-grid">
          
          {/* APPLICANT CHART */}
          <div className="dd-card dd-chart-card">
            <div className="dd-card-header">
              <h2>Applicants Overview</h2>
              <select className="dd-select">
                <option>Last 7 Days</option>
                <option>This Month</option>
              </select>
            </div>
            <div className="dd-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboardData?.chartData || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip 
                    cursor={{fill: 'rgba(255,255,255,0.03)'}}
                    contentStyle={{ backgroundColor: '#111', border: 'none', borderRadius: '8px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.5)' }}
                  />
                  <Bar dataKey="applicants" fill="#E10600" radius={[4, 4, 0, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ACTIVE ROLES LIST */}
          <div className="dd-card dd-roles-card">
            <div className="dd-card-header">
              <h2>My Active Roles</h2>
              <Link to="/dashboard/roles" className="dd-link-btn">View All</Link>
            </div>
            <div className="dd-roles-list">
              {dashboardData?.activeRoles?.map((role) => (
                <div className="dd-role-item" key={role.id}>
                  <div className="dd-role-info">
                    <div className="dd-role-icon-small"><Film size={16} /></div>
                    <div>
                      <h4 className="dd-role-title">{role.title}</h4>
                      <p className="dd-role-project">{role.project}</p>
                    </div>
                  </div>
                  <div className="dd-role-stats">
                    <div className="dd-applicant-count">
                      <strong>{role.applicants}</strong>
                      <span>Applicants</span>
                    </div>
                    <button className="dd-icon-btn"><MoreVertical size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: RECENT AUDITIONS TABLE */}
        <div className="dd-card dd-table-card">
          <div className="dd-card-header">
            <h2>Recent Auditions</h2>
            <div className="dd-table-filters">
              <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All</button>
              <button className={activeTab === "pending" ? "active" : ""} onClick={() => setActiveTab("pending")}>Pending</button>
              <button className={activeTab === "shortlisted" ? "active" : ""} onClick={() => setActiveTab("shortlisted")}>Shortlisted</button>
            </div>
          </div>

          <div className="dd-table-responsive">
            <table className="dd-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>CANDIDATE NAME</th>
                  <th>ROLE TITLE</th>
                  <th>PROJECT</th>
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData?.recentApplications?.map((row) => (
                  <tr key={row.id}>
                    <td className="dd-text-muted">{row.date}</td>
                    <td>
                      <div className="dd-candidate-cell">
                        <div className="dd-avatar">{row.candidate.charAt(0)}</div>
                        <span className="dd-candidate-name">{row.candidate}</span>
                      </div>
                    </td>
                    <td className="dd-font-medium">{row.role}</td>
                    <td className="dd-text-muted">{row.project}</td>
                    <td>{getTypeBadge(row.type)}</td>
                    <td>{getStatusBadge(row.status)}</td>
                    <td>
                      <button className="dd-action-btn" title="View Profile">
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DirectorDashboard;